import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  EnterpriseAssetLibraryAssetsDocument,
  LibraryAssetType,
  useEnterpriseAssetLibraryAssetsQuery,
  useEnterpriseAssetLibraryFoldersQuery,
  useEnterpriseAssetLibraryStatsQuery,
  useEnterpriseAssetLibraryCreateFolderMutation,
  useEnterpriseAssetLibraryDeleteFolderMutation,
  useEnterpriseAssetLibraryUpdateAssetMutation,
  useEnterpriseAssetLibraryDeleteAssetsMutation,
} from 'types/graphql';
import { useStores } from 'src/hooks/use-stores';

import type {
  LibraryAsset,
  Folder,
  ViewMode,
  LibraryFilters,
  AssetType,
  RawLibraryAsset,
  RawLibraryFolder,
} from '../types';

const PAGE_SIZE = 40;

const VALID_TYPES: AssetType[] = ['image', 'video', 'svg'];

// --- Query-param helpers ---------------------------------------------------

function filtersFromParams(params: URLSearchParams): LibraryFilters {
  const rawType = params.get('type');
  return {
    search: params.get('q') ?? '',
    type: rawType && VALID_TYPES.includes(rawType as AssetType) ? (rawType as AssetType) : 'all',
    folderId: params.get('folder') || null,
    tag: params.get('tag') || null,
  };
}

function pageFromParams(params: URLSearchParams): number {
  const p = parseInt(params.get('page') ?? '', 10);
  return Number.isFinite(p) && p > 0 ? p - 1 : 0; // URL is 1-based, state is 0-based
}

function filtersToParams(filters: LibraryFilters, page: number): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.search) p.set('q', filters.search);
  if (filters.type !== 'all') p.set('type', filters.type);
  if (filters.folderId) p.set('folder', filters.folderId);
  if (filters.tag) p.set('tag', filters.tag);
  if (page > 0) p.set('page', String(page + 1));
  return p;
}

// --- Data mappers ----------------------------------------------------------

type LibraryState = {
  filters: LibraryFilters;
  selectedIds: Set<string>;
  viewMode: ViewMode;
};

const mapAssetType = (t: string): AssetType => {
  if (t === 'video') return 'video';
  if (t === 'svg') return 'svg';
  return 'image';
};

const mapAsset = (raw: RawLibraryAsset): LibraryAsset => ({
  id: raw.id,
  name: raw.name,
  fileName: raw.fileName,
  url: raw.url,
  contentHash: raw.contentHash,
  type: mapAssetType(raw.type),
  mimeType: raw.mimeType,
  width: raw.width ?? null,
  height: raw.height ?? null,
  fileSize: raw.fileSize,
  folderId: raw.folderId ?? null,
  tags: raw.tags ?? [],
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
  createdBy: raw.createdBy ?? null,
  aiGenerated: Boolean(raw.aiGenerated),
});

const mapFolder = (raw: RawLibraryFolder): Folder => ({
  id: raw.id,
  name: raw.name,
  parentId: raw.parentId ?? null,
  assetCount: raw.assetCount ?? 0,
});

// ---------------------------------------------------------------------------

export function useLibraryState(enterpriseId: string) {
  const { apolloClient } = useStores();
  const history = useHistory();
  const location = useLocation();

  // Initialise from URL on mount
  const [filters, setFilters] = useState<LibraryFilters>(() => {
    const params = new URLSearchParams(location.search);
    return filtersFromParams(params);
  });
  const [page, setPage] = useState(() => {
    const params = new URLSearchParams(location.search);
    return pageFromParams(params);
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Push state → URL (skip the first render so we don't replace the initial URL)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const next = filtersToParams(filters, page);
    const current = new URLSearchParams(location.search);
    if (next.toString() !== current.toString()) {
      history.replace({ search: next.toString() });
    }
  }, [filters, page, history, location.search]);

  const filterVars = useMemo(
    () => ({
      search: filters.search || undefined,
      type: filters.type !== 'all' ? (filters.type as LibraryAssetType) : undefined,
      folderId: filters.folderId,
      tag: filters.tag,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [filters, page]
  );

  const assetsQuery = useEnterpriseAssetLibraryAssetsQuery({
    client: apolloClient,
    variables: { enterpriseId, filters: filterVars },
    fetchPolicy: 'cache-and-network',
    skip: !enterpriseId,
  });

  const foldersQuery = useEnterpriseAssetLibraryFoldersQuery({
    client: apolloClient,
    variables: { enterpriseId },
    fetchPolicy: 'cache-and-network',
    skip: !enterpriseId,
  });

  const [createFolderMutation] = useEnterpriseAssetLibraryCreateFolderMutation({ client: apolloClient });
  const [deleteFolderMutation] = useEnterpriseAssetLibraryDeleteFolderMutation({ client: apolloClient });
  const [updateAssetMutation] = useEnterpriseAssetLibraryUpdateAssetMutation({ client: apolloClient });
  const [deleteAssetsMutation] = useEnterpriseAssetLibraryDeleteAssetsMutation({ client: apolloClient });

  const assets = useMemo(() => (assetsQuery.data?.enterpriseAssetLibraryAssets.items ?? []).map(mapAsset), [
    assetsQuery.data,
  ]);
  const folders = useMemo(() => (foldersQuery.data?.enterpriseAssetLibraryFolders ?? []).map(mapFolder), [
    foldersQuery.data,
  ]);
  const total = assetsQuery.data?.enterpriseAssetLibraryAssets.total ?? 0;

  // Lightweight stats query for stable global counts (total + tag counts)
  const statsQuery = useEnterpriseAssetLibraryStatsQuery({
    client: apolloClient,
    variables: { enterpriseId },
    fetchPolicy: 'cache-and-network',
    skip: !enterpriseId,
  });

  const globalAssetCount = statsQuery.data?.enterpriseAssetLibraryStats.total ?? 0;

  const allTags = useMemo(() => {
    const tagCounts = statsQuery.data?.enterpriseAssetLibraryStats.tagCounts ?? [];
    const counts = new Map<string, number>();
    tagCounts.forEach((tc: { tag: string; count: number }) => counts.set(tc.tag, tc.count));
    return counts;
  }, [statsQuery.data]);

  const setFilter = useCallback((patch: Partial<LibraryFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setSelectedIds(new Set());
    setPage(0);
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const selectAll = useCallback(() => setSelectedIds(new Set(assets.map((a) => a.id))), [assets]);

  const refetchQueries = useMemo(
    () => [{ query: EnterpriseAssetLibraryAssetsDocument, variables: { enterpriseId, filters: filterVars } }],
    [enterpriseId, filterVars]
  );

  const createFolder = useCallback(
    async (name: string, parentId?: string | null) => {
      await createFolderMutation({
        variables: { enterpriseId, name, parentId: parentId ?? undefined },
        refetchQueries: ['EnterpriseAssetLibraryFolders'],
        awaitRefetchQueries: true,
      });
    },
    [createFolderMutation, enterpriseId]
  );

  const deleteFolder = useCallback(
    async (id: string) => {
      if (filters.folderId === id) setFilter({ folderId: null });
      await deleteFolderMutation({
        variables: { enterpriseId, id },
        refetchQueries: [
          'EnterpriseAssetLibraryFolders',
          'EnterpriseAssetLibraryAssets',
          'EnterpriseAssetLibraryStats',
        ],
        awaitRefetchQueries: true,
      });
    },
    [deleteFolderMutation, enterpriseId, filters.folderId, setFilter]
  );

  const updateAsset = useCallback(
    async (id: string, updates: { name?: string; folderId?: string | null; tags?: string[] }) =>
      updateAssetMutation({
        variables: { enterpriseId, id, ...updates },
        refetchQueries: [
          ...refetchQueries,
          ...(updates.folderId !== undefined ? ['EnterpriseAssetLibraryFolders'] : []),
          ...(updates.tags !== undefined || updates.folderId !== undefined ? ['EnterpriseAssetLibraryStats'] : []),
        ],
      }),
    [updateAssetMutation, enterpriseId, refetchQueries]
  );

  const deleteAssets = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      await deleteAssetsMutation({
        variables: { enterpriseId, ids },
        refetchQueries: [
          'EnterpriseAssetLibraryAssets',
          'EnterpriseAssetLibraryFolders',
          'EnterpriseAssetLibraryStats',
        ],
        awaitRefetchQueries: true,
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    },
    [deleteAssetsMutation, enterpriseId]
  );

  const refetchAssets = useCallback(() => {
    void assetsQuery.refetch();
    void foldersQuery.refetch();
    void statsQuery.refetch();
  }, [assetsQuery, foldersQuery, statsQuery]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const goToPage = useCallback(
    (p: number) => {
      setPage(Math.max(0, Math.min(p, totalPages - 1)));
      setSelectedIds(new Set());
    },
    [totalPages]
  );

  return {
    state: { filters, selectedIds, viewMode, assets, folders },
    loading: assetsQuery.loading || foldersQuery.loading,
    error: assetsQuery.error ?? foldersQuery.error,
    totalAssets: total,
    globalAssetCount,
    page,
    totalPages,
    allTags,
    setFilter,
    setViewMode,
    toggleSelect,
    clearSelection,
    selectAll,
    goToPage,
    createFolder,
    deleteFolder,
    updateAsset,
    deleteAssets,
    refetchAssets,
  };
}

export type { LibraryState };
